import React, { useState } from 'react';
import type { NamedEntity, VerifiedFact } from '../../types/graphql';

/**
 * Article → "Facts & entities" (Prototype v16): one section in the main
 * column. Entities as name chips by kind; the verified facts as a numbered
 * list — the fact, then its date, kind and who said it, with the sentence it
 * came from one click away — the first three, then "Show all N facts". Before
 * Prism AI has read the article, a line saying what it finds and the Extract
 * button.
 *
 * Chips gain × and "+ Add" while the article is being edited: changes save
 * with the article (the design shows them always; here nothing saves on its
 * own). The facts' own editor is the caller's, shown while editing.
 */

const FACTS_SHOWN = 3;

type Kind = { label: string; entities: NamedEntity[]; onChange: (next: NamedEntity[]) => void };

function EntityRow({ label, entities, onChange, editing }: Kind & { editing: boolean }) {
  const [adding, setAdding] = useState(false);
  const named = entities.filter((e) => e.name.trim());
  return (
    <div className="ink-fe-ent">
      <span className="ink-fe-ent-label">{label}</span>
      <div className="ink-fe-chips">
        {named.map((e, i) => (
          <span
            key={`${e.name}-${i}`}
            className="ink-fe-chip"
            title={e.role || e.context || undefined}
          >
            {e.name}
            {editing ? (
              <button
                type="button"
                aria-label={`Remove ${e.name}`}
                onClick={() => onChange(entities.filter((x) => x !== e))}
              >
                ×
              </button>
            ) : null}
          </span>
        ))}
        {!named.length && !adding ? <span className="ink-fe-none">None found</span> : null}
        {editing && adding ? (
          <input
            autoFocus
            className="ink-fe-add-input"
            placeholder="Name, then Enter"
            onBlur={() => setAdding(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') return setAdding(false);
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const name = e.currentTarget.value.trim();
              if (!name) return;
              if (!named.some((x) => x.name.toLowerCase() === name.toLowerCase())) {
                onChange([...entities, { name }]);
              }
              setAdding(false);
            }}
          />
        ) : null}
        {editing && !adding ? (
          <button type="button" className="ink-fe-add" onClick={() => setAdding(true)}>
            + Add
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function FactsEntities({
  facts,
  people,
  organizations,
  locations,
  editing,
  read,
  extracting,
  onExtract,
  sourceTypeLabel,
  factEditor,
}: {
  facts: VerifiedFact[];
  people: Omit<Kind, 'label'>;
  organizations: Omit<Kind, 'label'>;
  locations: Omit<Kind, 'label'>;
  editing: boolean;
  /** Prism AI has read this article (atomizedAt). */
  read: boolean;
  extracting: boolean;
  onExtract: () => void;
  sourceTypeLabel: (value?: string) => string;
  /** The facts editor, shown in place of the list while editing. */
  factEditor: React.ReactNode;
}) {
  const [all, setAll] = useState(false);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const list = facts.filter((f) => f.fact?.trim());
  const shown = all ? list : list.slice(0, FACTS_SHOWN);
  const hasAny =
    read ||
    list.length > 0 ||
    [people, organizations, locations].some((k) => k.entities.some((e) => e.name.trim()));

  return (
    <section data-part="facts" className="ink-detail-block ink-fe">
      <div className="ink-fe-head">
        <h3>Facts &amp; entities</h3>
        {hasAny ? <span>Found by Prism AI · LLMs cite these</span> : null}
      </div>
      {!hasAny && !editing ? (
        <div className="ink-fe-empty">
          <span>
            Not read yet. Prism AI lists the people, organizations, places and checkable facts in
            this article.
          </span>
          <button type="button" className="ink-btn" disabled={extracting} onClick={onExtract}>
            {extracting ? 'Extracting facts…' : 'Extract facts & entities'}
          </button>
        </div>
      ) : (
        <>
          <div className="ink-fe-sub">Entities</div>
          <div className="ink-fe-ents">
            <EntityRow label="People" editing={editing} {...people} />
            <EntityRow label="Organizations" editing={editing} {...organizations} />
            <EntityRow label="Locations" editing={editing} {...locations} />
          </div>

          <div className="ink-fe-subrow">
            <div className="ink-fe-sub">Verified facts</div>
            <span>
              {list.length} {list.length === 1 ? 'fact' : 'facts'}
            </span>
          </div>
          {editing ? (
            factEditor
          ) : list.length ? (
            <>
              <ol className="ink-fe-facts">
                {shown.map((f, i) => {
                  const meta = [f.date, sourceTypeLabel(f.source_type), f.attribution].filter(
                    Boolean
                  );
                  return (
                    <li key={i}>
                      <span className="ink-fe-n">#{i + 1}</span>
                      <div>
                        <div className="ink-fe-fact">{f.fact}</div>
                        <div className="ink-fe-meta">
                          {meta.map((m) => (
                            <span key={m}>{m}</span>
                          ))}
                          {f.source_quote ? (
                            <button
                              type="button"
                              aria-expanded={Boolean(open[i])}
                              onClick={() => setOpen((o) => ({ ...o, [i]: !o[i] }))}
                            >
                              {open[i] ? 'Hide original text' : 'Original text'}
                            </button>
                          ) : null}
                        </div>
                        {open[i] && f.source_quote ? (
                          <div className="ink-fe-orig">
                            <div>IN THE ARTICLE</div>
                            {f.source_quote}
                          </div>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {list.length > FACTS_SHOWN ? (
                <button type="button" className="ink-fe-more" onClick={() => setAll((v) => !v)}>
                  {all ? 'Show fewer' : `Show all ${list.length} facts`}
                </button>
              ) : null}
            </>
          ) : (
            <p className="ink-fe-none" style={{ marginTop: 10 }}>
              No facts found in the article.
            </p>
          )}
          <p className="ink-fe-note">
            Prism AI found these in the article’s text. Remove anything wrong with Edit. Write and
            reader questions use only these.
          </p>
        </>
      )}
    </section>
  );
}
